import {
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import {
  File,
  EyeOff,
  ArrowLeft,
  HatGlasses,
  Calendar,
  CalendarClock,
} from "lucide-react";
import { Task, TaskBlock, TaskState, StageTransition } from "protogen/tasks_pb";
import { DelegationTask } from "protogen/delegations_pb";
import { CalendarEvent, EventNotice } from "protogen/calendar_pb";
import {
  useUpdateTask,
  useCreateTaskBlock,
  useCreateMemberTaskBlock,
  useUpdateMemberTask,
  useCreateTask,
  useCreateMemberTask,
  useUpdateTaskStage,
} from "services/tasks";
import {
  EphemeralTask,
  TaskFormErrors,
  updates,
  validateTask,
  taskToEphemeral,
} from "../../types/tasks";
import SnackPack, { SnackbarMessage } from "../common/SnackPack";
import TimeTrackingBlock from "./blocks/TimeTrackingBlock";
import DescriptionBlock, { Handle } from "./blocks/DescriptionBlock";
import TaskRequirementsBlock from "./blocks/TaskRequirementsBlock";
import ConversationBookmarkBlock from "./blocks/ConversationBookmarkBlock";
import DocumentBlock from "./blocks/DocumentBlock";
import AgentBlock from "./blocks/AgentBlock";
import DelegationBlock from "./blocks/DelegationBlock";
import TaskEventBlock from "./blocks/TaskEventBlock";
import TaskReminderDialog from "./TaskReminderDialog";
import AddEventDialog from "../calendar/AddEventDialog";
import EditEventDialog from "../calendar/EditEventDialog";
import {
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useListEventsBySource,
  useListExternalEventsBySource,
  useUpdateCalendarEvent,
} from "services/calendar";
import {
  CreateCalendarEventRequest,
  DeleteCalendarEventRequest,
  ListEventsBySourceRequest,
  ListExternalEventsBySourceRequest,
  UpdateCalendarEventRequest,
} from "protogen/calendar_service_pb";
import { EventReminder } from "protogen/calendar_pb";
import { ContentUpdates } from "protogen/notes_pb";
import { useIsNewTask } from "./utils";
import TimeCompletion from "./TimeCompletion";
import { celebrate, getElementCenter } from "../../common/celebrations";
import useIsMobile from "../hooks/useIsMobile";
import PrioritySelect from "../creation/PrioritySelect";
import { BlockHandle } from "./blocks/utils";
import { ActiveSparkles } from "./TaskSuggestions";
import WebResearchBlock from "./blocks/WebResearchBlock";
import SimpleTaskDocument from "./SimpleTaskDocument";
import { RichContent } from "../editor/utils";
import CurrentUserAvatar from "../common/CurrentUserAvatar";
import { dateStringToDayAndDate } from "./utils";
import { Family } from "protogen/advisors_service_pb";
import { getFormattedLocation } from "../family/utils";
import { CreateTaskRequest } from "protogen/tasks_service_pb";
import { StatusContext } from "components/context/StatusContextProvider";
import RoundedDropDown, { Option } from "../common/RoundedDropDown";
import { stripImgSrc } from "../../common/utils";
import SimpleRichEditor from "components/editor/SimpleRichEditor";
import DropdownMenu from "../common/DropdownMenu";
import { PictureInPictureContentProvider } from "components/context/PictureInPictureContext";
import {
  CurrentStageChip,
  getScrollableParent,
  StageValue,
} from "./workflowStates";
import StageTransitionBlock from "./blocks/StageTransitionBlock";
import { isFeatureEnabled } from "components/helpers/GatedComponent";
import StartAgentPrompt from "./StartAgentPrompt";
import TaskRemindersDropdown from "./TaskRemindersDropdown";
import DueDatePicker from "./DueDatePicker";
import { LIGHTNING_DETECTION_STAGE } from "types/delegation";
import Checkbox from "components/common/Checkbox";

// Merge stage transitions, calendar events, external events, and delegation tasks into the blocks list chronologically
// while respecting manually moved blocks (those with lastUpdatedSec > createdSec)
const mergeFeedItems = (
  task: Task,
  calendarEvents: CalendarEvent[],
  externalEvents: EventNotice[],
  delegationTasks: DelegationTask[],
): (
  | TaskBlock
  | StageTransition
  | CalendarEvent
  | EventNotice
  | DelegationTask
)[] => {
  const blocks = [...task.blocks];
  const stageTransitions = [...(task.stageTransitions || [])];

  // Create a combined list with timestamps for sorting
  const combinedItems: {
    item:
      | TaskBlock
      | StageTransition
      | CalendarEvent
      | EventNotice
      | DelegationTask;
    timestamp: number;
    type:
      | "block"
      | "stageTransition"
      | "calendarEvent"
      | "externalEvent"
      | "delegationTask";
  }[] = [];

  // Add blocks with their creation or last updated time (whichever is later to respect manual moves)
  blocks.forEach((block) => {
    const blockMetadata = JSON.parse(block.metadata);
    const isInternalWorkspace =
      !blockMetadata.isShared && blockMetadata.isDescription;
    const timestamp = isInternalWorkspace
      ? Number(block.createdSec)
      : Math.max(Number(block.createdSec), Number(block.lastUpdatedSec));
    combinedItems.push({ item: block, timestamp, type: "block" });
  });

  // Add stage transitions with their transition time
  stageTransitions.forEach((transition) => {
    combinedItems.push({
      item: transition,
      timestamp: Number(transition.transitionTimeSec),
      type: "stageTransition",
    });
  });

  // Add calendar events with their creation time
  calendarEvents.forEach((event) => {
    combinedItems.push({
      item: event,
      timestamp: Number(event.createdSec),
      type: "calendarEvent",
    });
  });

  // Add external events with their start time
  externalEvents.forEach((event) => {
    combinedItems.push({
      item: event,
      timestamp: Number(event.startSec),
      type: "externalEvent",
    });
  });

  // Add delegation tasks with their creation time
  delegationTasks.forEach((delegationTask) => {
    combinedItems.push({
      item: delegationTask,
      timestamp: Number(delegationTask.createdSec),
      type: "delegationTask",
    });
  });

  // Sort by timestamp (chronological order)
  combinedItems.sort((a, b) => a.timestamp - b.timestamp);

  return combinedItems.map((item) => item.item);
};

const LightningBanner = ({ title, body }: { title: string; body: string }) => (
  <Box
    sx={{
      padding: "16px",
      borderRadius: "12px",
      background: "#F6F8FA",
      border: "1px solid #E5E7EB",
      marginBottom: "20px",
    }}
  >
    <Typography variant="bodyHeavy" sx={{ marginBottom: "4px" }}>
      {title}
    </Typography>
    <Typography variant="bodySmall" color="text.secondary">
      {body}
    </Typography>
  </Box>
);

const LightningDetectionNotice = () => (
  <LightningBanner
    title="Reviewing your lightning request"
    body="We're checking whether this can become a lightning task and will route it automatically."
  />
);

const LightningNeedsAdvisorNotice = () => (
  <LightningBanner
    title="Lightning task needs advisor follow-up"
    body="Your client requested this as a ligntning task but it was determined to be too complex and requires your follow up. Please review it soon."
  />
);

const isDescriptionLikeLightningBlock = (block: TaskBlock) => {
  if (block.type === "description") {
    return true;
  }
  if (block.type !== "document" || !block.metadata) {
    return false;
  }
  try {
    return !!JSON.parse(block.metadata).isDescription;
  } catch {
    return false;
  }
};

const isLightningTaskOriginBlock = (block: TaskBlock) => {
  if (block.type !== "agent" || !block.metadata) {
    return false;
  }
  try {
    return !!JSON.parse(block.metadata).isLightningTask;
  } catch {
    return false;
  }
};

// Wrapper component to properly handle useRef hook for document blocks
const DocumentBlockWrapper = ({
  block,
  task,
  addBlockRef,
  i,
  doToast,
  accountType,
  onDeleteTaskBlock,
}: {
  block: TaskBlock;
  task: Task;
  addBlockRef: (ref: BlockHandle | null, idx: number) => void;
  i: number;
  doToast: (key: bigint, content?: string) => void;
  accountType: "advisor" | "member";
  onDeleteTaskBlock: { (ref: string): void };
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <PictureInPictureContentProvider
      contentRef={contentRef}
      id={`document-block-${block.ref}`}
    >
      <DocumentBlock
        ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
        key={block.ref}
        block={block}
        task={task}
        onUpdate={(n, c) => {
          doToast(n, c);
        }}
        accountType={accountType}
        onDeleteTaskBlock={onDeleteTaskBlock}
        contentRef={contentRef}
      />
    </PictureInPictureContentProvider>
  );
};

const isDescriptionBlock = (block: TaskBlock): boolean => {
  try {
    const metadata = JSON.parse(block.metadata);
    return !!metadata.isDescription;
  } catch (e) {
    return false;
  }
};

const moveClientDescriptionToFront = (blocks: TaskBlock[]): TaskBlock[] => {
  const reorderedBlocks = [...blocks];
  const descriptionBlocks = reorderedBlocks.filter((b) =>
    isDescriptionBlock(b),
  );

  if (descriptionBlocks.length > 0) {
    return [
      ...descriptionBlocks,
      ...reorderedBlocks.filter((b) => !descriptionBlocks.includes(b)),
    ];
  }

  return reorderedBlocks;
};

const BlockSection = ({
  task,
  detailsRef,
  doToast,
  accountType = "advisor",
  family,
  updateTask,
  onDeleteTaskBlock,
  refreshTask,
  refreshEvents,
  delegationTasks,
  taskCalendarEvents = [],
  taskExternalEvents = [],
}: {
  task: Task;
  detailsRef: RefObject<Handle>;
  doToast: (key: bigint, content?: string) => void;
  accountType: "advisor" | "member";
  family?: Family;
  updateTask: (t: Task) => void;
  onDeleteTaskBlock: { (ref: string): void };
  refreshTask: () => Promise<void>;
  refreshEvents: () => void;
  delegationTasks?: DelegationTask[];
  taskCalendarEvents?: CalendarEvent[];
  taskExternalEvents?: EventNotice[];
}) => {
  const [latestRefs, setLatestRefs] = useState<BlockHandle[]>([]);
  const blockRefs = useRef<BlockHandle[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  const addBlockRef = (ref: BlockHandle | null, idx: number) => {
    if (ref) {
      blockRefs.current[idx] = ref;
    }
  };
  const moveToFront = (arr: TaskBlock[], type: string) => {
    // Filter out the items that match the value
    const matchingItems = arr.filter((item) => item.type === type);
    // Filter out the items that don't match the value
    const nonMatchingItems = arr.filter((item) => item.type !== type);
    // Concatenate the matching items at the front, followed by the non-matching items
    return [...matchingItems, ...nonMatchingItems];
  };

  useEffect(() => {
    setLatestRefs(blockRefs.current);
  }, [blockRefs.current]);

  // Get chronologically ordered blocks, stage transitions, calendar events, external events, and delegation tasks
  const mergedItems = mergeFeedItems(
    task,
    taskCalendarEvents,
    taskExternalEvents,
    delegationTasks || [],
  );

  // Apply existing ordering logic to just the blocks
  const blocks = mergedItems.filter((item) => "type" in item) as TaskBlock[];
  let reoderedBlocks = moveClientDescriptionToFront(blocks);
  reoderedBlocks = moveToFront(reoderedBlocks, "description");
  reoderedBlocks = moveToFront(reoderedBlocks, "time-tracking");

  // Now rebuild the merged list with properly ordered blocks (keep events, transitions, and delegation tasks in chronological order)
  const finalOrderedItems: (
    | TaskBlock
    | StageTransition
    | CalendarEvent
    | EventNotice
    | DelegationTask
  )[] = [];
  let blockIndex = 0;

  for (const item of mergedItems) {
    if ("stage" in item && "transitionTimeSec" in item) {
      // This is a stage transition
      finalOrderedItems.push(item);
    } else if ("startSec" in item) {
      // This is a calendar event - keep in chronological order
      finalOrderedItems.push(item);
    } else if ("serviceType" in item) {
      // This is a delegation task - keep in chronological order
      finalOrderedItems.push(item);
    } else {
      // This is a block - use the reordered version
      if (blockIndex < reoderedBlocks.length) {
        finalOrderedItems.push(reoderedBlocks[blockIndex]);
        blockIndex++;
      }
    }
  }

  const blockComponents = finalOrderedItems.flatMap((item, i) => {
    // Check if this is a delegation task
    if ("serviceType" in item) {
      const delegationTask = item as DelegationTask;
      return (
        <Box id={`delegation-${delegationTask.ref}`} key={delegationTask.ref}>
          <DelegationBlock delegationTask={delegationTask} family={family} />
        </Box>
      );
    }

    // Check if this is a stage transition
    if ("stage" in item && "transitionTimeSec" in item) {
      let transitionerName = "";
      if (item.transitionedByRef === task.createdBy?.ref) {
        transitionerName = task.createdBy?.displayName || "";
      } else if (item.transitionedByRef === task.assignee?.ref) {
        transitionerName = task.assignee?.displayName || "";
      } else if (
        family?.familyMembers?.find((m) => m.ref === item.transitionedByRef)
      ) {
        const transitioner = family?.familyMembers?.find(
          (m) => m.ref === item.transitionedByRef,
        );
        transitionerName = transitioner?.displayName || "";
      }
      return (
        <StageTransitionBlock
          key={`${item.stage}-${item.transitionTimeSec}`}
          accountType={accountType}
          transitionerName={transitionerName}
          stage={item.stage}
          transitionTime={new Date(1000 * Number(item.transitionTimeSec))}
          stageDate={item.stageDate}
        />
      );
    }

    // Check if this is an external event (EventNotice)
    if ("noticeType" in item) {
      const event = item as EventNotice;

      // Skip events with invalid dates
      if (!event.startSec || Number(event.startSec) === 0) {
        console.warn("Skipping external event with invalid startSec:", event);
        return null;
      }

      // Create a synthetic TaskBlock from the external event for TaskEventBlock
      const syntheticBlock = new TaskBlock({
        ref: event.eventRef,
        type: "task_event",
        createdSec: BigInt(event.startSec),
        lastUpdatedSec: BigInt(event.startSec),
        metadata: JSON.stringify({
          title: event.title,
          event_datetime: new Date(Number(event.startSec) * 1000).toISOString(),
          all_day: event.allDay,
          isExternal: true,
          externalCalendarName: event.externalCalendarName,
        }),
      });

      return (
        <Box id={`event-${event.eventRef}`} key={event.eventRef}>
          <TaskEventBlock
            block={syntheticBlock}
            task={task}
            accountType={accountType}
            onUpdate={() => {
              refreshTask();
              refreshEvents();
            }}
          />
        </Box>
      );
    }

    // Check if this is a calendar event
    if ("startSec" in item) {
      const event = item as CalendarEvent;

      // Members should not see task reminders, but can see task events
      if (
        accountType === "member" &&
        event.sourceEntityType === "task_reminder"
      ) {
        return null;
      }

      // Skip events with invalid dates
      if (!event.startSec || Number(event.startSec) === 0) {
        console.warn("Skipping event with invalid startSec:", event);
        return null;
      }

      // Create a synthetic TaskBlock from the calendar event for TaskEventBlock
      const syntheticBlock = new TaskBlock({
        ref: event.ref,
        type: "task_event",
        createdSec: event.createdSec,
        lastUpdatedSec: event.lastUpdatedSec,
        metadata: JSON.stringify({
          title: event.title,
          event_datetime: new Date(Number(event.startSec) * 1000).toISOString(),
          all_day: event.allDay,
        }),
      });

      return (
        <Box id={`event-${event.ref}`} key={event.ref}>
          <TaskEventBlock
            block={syntheticBlock}
            task={task}
            accountType={accountType}
            onUpdate={() => {
              refreshTask();
              refreshEvents();
            }}
            event={event}
          />
        </Box>
      );
    }

    // This is a regular block
    const block = item as TaskBlock;
    if (block.type === "time-tracking") {
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <TimeTrackingBlock
            ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
            block={block}
            task={task}
            onUpdate={(n) => {
              doToast(n);
            }}
            updateTask={updateTask}
          />
        </Box>
      );
    } else if (block.type === "description") {
      return [
        <Box id={`block-${block.ref}`} key={block.ref}>
          <DescriptionBlock
            ref={detailsRef}
            block={block}
            task={task}
            onUpdate={(n) => {
              doToast(n);
            }}
          />
        </Box>,
      ];
    } else if (block.type === "conversation-bookmark") {
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <ConversationBookmarkBlock
            block={block}
            task={task}
            accountType={accountType}
            ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
          />
        </Box>
      );
    } else if (block.type === "requirement-list") {
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <TaskRequirementsBlock
            ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
            block={block}
            task={task}
            accountType={accountType}
            onUpdate={(n) => {
              doToast(n);
            }}
          />
        </Box>
      );
    } else if (block.type === "internet-research") {
      // if (isFeatureEnabled("faye.task-agents", true)) {
      //   return null;
      // }
      const mappedAddresses: [string, string][] = [];
      if (family) {
        mappedAddresses.push([
          family.name,
          getFormattedLocation(family.address),
        ]);
      }
      const details = reoderedBlocks.find((b) => b.type === "requirement-list");
      if (details && details.content) {
        const parsed = JSON.parse(details.content);
        const labelRegex =
          /\b(home|location|address|office|place|residence|site|spot)\b/i;
        for (const d of parsed?.content[0]?.attrs?.details || []) {
          if (d?.value && d?.description.match(labelRegex)) {
            mappedAddresses.push([d.description, d.value]);
          }
        }
      }
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <WebResearchBlock
            block={block}
            task={task}
            ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
            mappedAddresses={mappedAddresses}
          />
        </Box>
      );
    } else if (block.type === "document") {
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <DocumentBlockWrapper
            block={block}
            task={task}
            addBlockRef={addBlockRef}
            i={i}
            doToast={doToast}
            accountType={accountType}
            onDeleteTaskBlock={onDeleteTaskBlock}
          />
        </Box>
      );
    } else if (block.type === "agent") {
      // For clients (members), only show if they have faye.member-fayebot feature flag AND the block has isMemberVisible
      if (accountType === "member") {
        if (task.stage === LIGHTNING_DETECTION_STAGE) {
          return null;
        }
        const blockMetadata = block.metadata ? JSON.parse(block.metadata) : {};
        if (!blockMetadata.isMemberVisible) {
          return null;
        }
      }
      return (
        <Box id={`block-${block.ref}`} key={block.ref}>
          <AgentBlock
            ref={(ref: BlockHandle | null) => addBlockRef(ref, i)}
            block={block}
            task={task}
            accountType={accountType}
            onDeleteTaskBlock={onDeleteTaskBlock}
            refreshTask={refreshTask}
            delegationTasks={delegationTasks}
          />
        </Box>
      );
    }
  });

  // Locally poll for changes to the subcomponent loading states.
  useEffect(() => {
    if (blocksLoading) {
      const interval = setInterval(() => {
        if (!(latestRefs || []).some((r) => r.isPolling?.())) {
          setBlocksLoading(false);
        }
      }, 200);

      return () => clearInterval(interval); // Clear interval on component unmount
    }
  }, [blocksLoading, latestRefs]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      {blockComponents}
      {blocksLoading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            marginBottom: "24px",
          }}
        >
          <ActiveSparkles />
          <Typography variant="bodySmall">Loading suggestions...</Typography>
        </Box>
      )}
    </Box>
  );
};

interface TaskPropertiesProps {
  formData: EphemeralTask;
  updateFormData: (f: EphemeralTask) => void | Promise<void>;
  errors: TaskFormErrors | null;
  onNewlyCompleted: () => void;
  accountType: "advisor" | "member";
  task: Task;
  taskCalendarEvents?: CalendarEvent[];
  onAddEvent?: () => void;
  onEditEvent?: (event: CalendarEvent) => void;
}

const TaskProperties = ({
  errors,
  formData,
  updateFormData,
  accountType,
  task,
  taskCalendarEvents = [],
  onAddEvent,
  onEditEvent,
}: TaskPropertiesProps) => {
  const isMobile = useIsMobile();

  return (
    <Box>
      {accountType === "advisor" ? (
        <Box
          sx={{
            display: "flex",
            gap: isMobile ? "8px" : "14px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: isMobile ? "8px" : "14px",
            }}
          >
            <Box sx={{ width: isMobile ? "50%" : "160px" }}>
              <PrioritySelect
                disabled={false}
                error={errors?.priority}
                selectedPriority={formData.priority || null}
                onChange={(p) => updateFormData({ priority: p || undefined })}
              />
            </Box>
            <Box sx={{ width: isMobile ? "50%" : "160px" }}>
              {task.ref ? (
                <TaskRemindersDropdown
                  task={task}
                  events={taskCalendarEvents}
                  onAddEvent={onAddEvent}
                  onEditEvent={onEditEvent}
                />
              ) : (
                <DueDatePicker
                  value={formData.dueDate}
                  onChange={(dateString) =>
                    updateFormData({ dueDate: dateString })
                  }
                  error={errors?.dueDate}
                  disabled={false}
                />
              )}
            </Box>
          </Box>
          {task.ref && (
            <Box
              sx={{ width: isMobile ? "100%" : undefined, minWidth: "160px" }}
            >
              <CurrentStageChip
                clickTargetId={`task-status-dropdown-${formData.ref}`}
                stage={task.stage as StageValue}
                stageDate={task.stageDate}
                accountType={accountType}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Box sx={{ width: "160px" }}>
            <PrioritySelect
              sx={{ height: "44px" }}
              disabled={false}
              error={errors?.priority}
              selectedPriority={formData.priority || null}
              onChange={(p) => updateFormData({ priority: p || undefined })}
            />
          </Box>
          {task.ref && (
            <Box sx={{ minWidth: "160px" }}>
              <CurrentStageChip
                clickTargetId={`task-status-dropdown-${formData.ref}`}
                stage={task.stage as StageValue}
                stageDate={task.stageDate}
                accountType={accountType}
              />
            </Box>
          )}
          {formData.dueDate && (
            <Chip
              label={
                "Due: " +
                dateStringToDayAndDate(formData.dueDate || "", false)[1]
              }
              sx={{
                color: "#3D3D3D",
                fontWeight: "600",
                height: "44px",
                background: "#F5F5F5",
                borderRadius: "100px",
                fontSize: isMobile ? "14px" : "16px",
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

interface FooterProps {
  formData: EphemeralTask;
  updateFormData: (f: EphemeralTask) => void | Promise<void>;
  doneButtonRef: RefObject<HTMLButtonElement>;
  onNewlyCompleted: () => void;
  onCreateDocument: () => void;
  onCreateAgent: () => void;
  onAddTaskEvent: () => void;
  onAddTaskReminder: () => void;
}

const Footer = ({
  onCreateDocument,
  onCreateAgent,
  onAddTaskEvent,
  onAddTaskReminder,
}: FooterProps) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "21px",
      }}
    >
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "12px",
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button
              variant="text"
              color="primary"
              startIcon={<ArrowLeft />}
              sx={{
                ".MuiButton-icon": {
                  margin: 0,
                },
              }}
              onClick={() => {
                if (window.history.state && window.history.state.idx > 0) {
                  navigate(-1); // Go back
                } else {
                  navigate("/tasks"); // Go to fallback
                }
              }}
            >
              Tasks
            </Button>
            <DropdownMenu
              buttonContent="Add New"
              dropdownIcon={true}
              options={[
                // {
                //   title: "Start AI Research",
                //   IconComponent: ScanSearch,
                //   onClick: () => onCreateDocument(true),
                // },
                {
                  title: "New document",
                  IconComponent: File,
                  onClick: () => onCreateDocument(),
                },
                {
                  title: "Add Reminder",
                  IconComponent: CalendarClock,
                  onClick: onAddTaskReminder,
                },
                {
                  title: "Add Event",
                  IconComponent: Calendar,
                  onClick: onAddTaskEvent,
                },
                // Still leave on for admins
                ...(isFeatureEnabled("faye.task-agents", true)
                  ? [
                      {
                        title: "Agent",
                        IconComponent: HatGlasses,
                        onClick: () => onCreateAgent(),
                      },
                    ]
                  : []),
              ]}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type Props = {
  task: Task;
  updateTask: (t: Task) => void;
  updateBufferMsec?: number;
  accountType?: "member" | "advisor";
  family?: Family;
  isUnsavedTask?: boolean;
  initialIsLightningTask?: boolean;
  refreshTask: () => Promise<void>;
  delegationTasks?: DelegationTask[];
};
export default ({
  task,
  updateTask,
  updateBufferMsec = 2500,
  accountType = "advisor",
  family,
  isUnsavedTask = false,
  initialIsLightningTask = false,
  refreshTask,
  delegationTasks,
}: Props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { families } = useContext(StatusContext);
  const isFresh = useIsNewTask();
  const firstRender = useRef(true);
  const [searchParams] = useSearchParams();
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const [formErrors, setFormErrors] = useState<TaskFormErrors | null>(null);
  const [newlyCompleted, setNewlyCompleted] = useState(false);
  const [_isUnsavedTask, _setIsUnsavedTask] = useState(isUnsavedTask);
  const [isLightningTask, setIsLightningTask] = useState(
    initialIsLightningTask,
  );
  const [selectedFamilyRef, setSelectedFamilyRef] = useState<string>("");
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const [content, setContent] = useState<RichContent | null>(null);
  const [formData, setFormData] = useState<EphemeralTask>(
    taskToEphemeral(task),
  );
  const taskSuggestionDetails =
    // @ts-ignore
    isUnsavedTask && task.details ? task.details.payload : "";
  const notesRef = useRef<Handle>(null);
  const useUpdateTaskFn =
    accountType === "advisor" ? useUpdateTask : useUpdateMemberTask;
  const { request } = useUpdateTaskFn(() => {});
  const createTaskBlockRequestFn =
    accountType === "advisor" ? useCreateTaskBlock : useCreateMemberTaskBlock;
  const { request: createTaskBlockRequest, loading: createTaskBlockLoading } =
    createTaskBlockRequestFn((r) => {
      if (!r?.block) return;
      const updatedTask = task.clone();
      updatedTask.blocks = [...updatedTask.blocks, r.block];
      updateTask(updatedTask);
    });

  const createTaskRequestFn =
    accountType === "advisor" ? useCreateTask : useCreateMemberTask;

  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showFullEventDialog, setShowFullEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [editingFullEvent, setEditingFullEvent] =
    useState<CalendarEvent | null>(null);
  const { request: updateTaskStage, loading: updateTaskStageLoading } =
    useUpdateTaskStage();

  const { request: createCalendarEvent, loading: createEventLoading } =
    useCreateCalendarEvent((_response) => {
      // Don't refresh during initial task creation - handled separately
      if (!isUnsavedTask) {
        refreshTask();
      }
      if (task.ref) {
        listEventsBySource(
          new ListEventsBySourceRequest({
            sourceEntityType: "task_reminder",
            sourceEntityRef: task.ref,
          }),
        );
      }
      setShowEventDialog(false);
    });

  const { request: updateCalendarEvent, loading: updateEventLoading } =
    useUpdateCalendarEvent((_response) => {
      refreshTask();
      if (task.ref) {
        listEventsBySource(
          new ListEventsBySourceRequest({
            sourceEntityType: "task_reminder",
            sourceEntityRef: task.ref,
          }),
        );
      }
      setEditingEvent(null);
    });

  const [taskCalendarEvents, setTaskCalendarEvents] = useState<CalendarEvent[]>(
    [],
  );
  const [taskExternalEvents, setTaskExternalEvents] = useState<EventNotice[]>(
    [],
  );
  const isLightningTaskOrigin = task.blocks.some(isLightningTaskOriginBlock);
  const isLightningDetectionMemberView =
    accountType === "member" && task.stage === LIGHTNING_DETECTION_STAGE;
  const isLightningDetectionAdvisorView =
    accountType === "advisor" &&
    isLightningTaskOrigin &&
    task.stage === LIGHTNING_DETECTION_STAGE;
  const isLightningAdvisorFollowupView =
    accountType === "advisor" &&
    isLightningTaskOrigin &&
    task.stage !== LIGHTNING_DETECTION_STAGE &&
    task.stage !== "done" &&
    (delegationTasks || []).length === 0;
  const lightningDetectionTask = useMemo(() => {
    if (!isLightningDetectionMemberView) return task;
    const filteredTask = task.clone();
    filteredTask.blocks = task.blocks.filter(isDescriptionLikeLightningBlock);
    filteredTask.stageTransitions = [];
    return filteredTask;
  }, [task, isLightningDetectionMemberView]);
  const { request: listEventsBySource } = useListEventsBySource((response) => {
    setTaskCalendarEvents(response.events);
  });
  const { request: listExternalEventsBySource } = useListExternalEventsBySource(
    (response) => {
      setTaskExternalEvents(response.events);
    },
  );

  const refreshEvents = () => {
    if (!task.ref) return;
    if (accountType === "advisor") {
      listEventsBySource(
        new ListEventsBySourceRequest({
          sourceEntityType: "task_reminder",
          sourceEntityRef: task.ref,
        }),
      );
    }
    listEventsBySource(
      new ListEventsBySourceRequest({
        sourceEntityType: "task_event",
        sourceEntityRef: task.ref,
      }),
    );
  };

  const { request: deleteCalendarEvent } = useDeleteCalendarEvent(() => {
    refreshTask();
    refreshEvents();
  });

  useEffect(() => {
    if (task.ref) {
      // Fetch events based on account type
      const requests = [];

      // Only advisors fetch reminders
      if (accountType === "advisor") {
        requests.push(
          listEventsBySource(
            new ListEventsBySourceRequest({
              sourceEntityType: "task_reminder",
              sourceEntityRef: task.ref,
            }),
          ),
        );
      }

      // Both members and advisors fetch task events
      requests.push(
        listEventsBySource(
          new ListEventsBySourceRequest({
            sourceEntityType: "task_event",
            sourceEntityRef: task.ref,
          }),
        ),
      );

      // Both members and advisors fetch external events
      requests.push(
        listExternalEventsBySource(
          new ListExternalEventsBySourceRequest({
            sourceEntityType: "task_event",
            sourceEntityRef: task.ref,
          }),
        ),
      );

      Promise.all(requests).then((responses) => {
        // Parse responses based on account type
        let remindersResp, eventsResp, externalEventsResp;

        if (accountType === "advisor") {
          [remindersResp, eventsResp, externalEventsResp] = responses;
        } else {
          // Members only get events and external events
          [eventsResp, externalEventsResp] = responses;
        }

        // Merge and deduplicate internal events by ref
        const allEvents = [
          ...(remindersResp?.events || []),
          ...(eventsResp?.events || []),
        ];
        const uniqueEvents = allEvents.filter(
          (event, index, self) =>
            index === self.findIndex((e) => e.ref === event.ref),
        );
        setTaskCalendarEvents(uniqueEvents);
        setTaskExternalEvents(externalEventsResp?.events || []);
      });
    }
  }, [task.ref, task.lastUpdatedSec, accountType]);

  const { request: createTaskRequest, loading: createLoading } =
    createTaskRequestFn(async (r) => {
      if (!r.task?.ref) return;
      updateTask(r.task);
      setFormData(taskToEphemeral(r.task));
      firstRender.current = true;
      _setIsUnsavedTask(false);
      navigate(`/tasks/${encodeURIComponent(r.task?.ref)}`, { replace: true });

      // If task was created with a due date, create the calendar event
      if (r.task.dueDate && accountType === "advisor") {
        const [month, day, year] = r.task.dueDate.split("/").map(Number);
        const dueDateTime = new Date(year, month - 1, day, 9, 0, 0); // Default to 9 AM
        const timestampSec = BigInt(Math.floor(dueDateTime.getTime() / 1000));

        await createCalendarEvent(
          new CreateCalendarEventRequest({
            familyRef: r.task.familyRef,
            title: "Due date",
            description: new ContentUpdates({ contentType: "json" }),
            startSecUTC: timestampSec,
            endSecUTC: timestampSec,
            allDay: false,
            displayTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            reminders: [new EventReminder({ value: 0, unit: "minutes" })],
            sourceEntityType: "task_reminder",
            sourceEntityRef: r.task.ref,
            meta: JSON.stringify({ taskEventType: "dueDate" }),
          }),
        );

        // After creating the calendar event, fetch the events for this task
        listEventsBySource(
          new ListEventsBySourceRequest({
            sourceEntityType: "task_reminder",
            sourceEntityRef: r.task.ref,
          }),
        );
      }
    });

  const _validate = (payload: EphemeralTask) => {
    const errors = validateTask(payload);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onCreateTaskBlock = async (
    type: string,
    isShareable: boolean,
    content?: RichContent,
    isRichDocument?: boolean,
    isAgent?: boolean,
    scrollToEnd?: boolean,
  ) => {
    let metadata: { [key: string]: any } = {
      isShared: isShareable,
      isRichDocument: isRichDocument,
    };
    if (isAgent) {
      metadata = {
        ...metadata,
        isProcessing: true,
        processingStates: [],
        title: "Agent",
        isMemberVisible: true,
      };
    }
    const newBlock = await createTaskBlockRequest({
      taskRef: task.ref,
      type: type,
      content: stripImgSrc(content?.html || ""),
      textContent: content?.text || "",
      attachments: content?.attachments || [],
      contentType: "html",
      metadata: JSON.stringify(metadata),
    });
    if (scrollToEnd) {
      // Scroll to the end
      const ele = document.getElementById("task-details-panel");
      const scrollableParent = getScrollableParent(ele || undefined);
      scrollableParent.scrollTo({
        top: scrollableParent.scrollHeight,
        behavior: "smooth",
      });
    }
    return newBlock;
  };
  const onDeleteTaskBlock = async (ref: string) => {
    const updatedTask = task.clone();
    updatedTask.blocks = updatedTask.blocks.filter((b) => b.ref !== ref);
    updateTask(updatedTask);
  };
  // Use the debounce function to save data with a delay
  const handleUpdateTask = async (task: Task, payload: EphemeralTask) => {
    if (_validate(payload)) {
      const result = await request(updates(task, payload));
      if (result) {
        updateTask(result.task);
        doToast(result.task?.lastUpdatedSec || BigInt(0));
      }
    }
  };

  const doToast = (key: bigint, content?: string) => {
    setSnackPack((p) => [
      ...p,
      {
        message: content || "Changes saved!",
        saveNotification: true,
        key: `${key}`,
      },
    ]);
  };
  const debouncedUpdateTask = useMemo(() => {
    return debounce(
      async (task, p) => {
        await handleUpdateTask(task, p);
      },
      updateBufferMsec,
      { trailing: true },
    );
  }, []);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (_isUnsavedTask) return;
    debouncedUpdateTask(task, formData);
  }, [formData]);

  useEffect(() => {
    const taskAgeSecs = new Date().getTime() / 1000 - Number(task.createdSec);
    // If the page is refreshed, lets not show this.
    if (isFresh && taskAgeSecs < 30) {
      setSnackPack((p) => [
        ...p,
        {
          message: "Task created!",
          saveNotification: true,
          key: `new-task`,
        },
      ]);
    }
    if (accountType !== "advisor") {
      return;
    }
  }, [task.ref]);

  useEffect(() => {
    _setIsUnsavedTask(isUnsavedTask);
  }, [isUnsavedTask]);

  // Reset newlyCompleted when task becomes active again
  useEffect(() => {
    if (task.stage !== "done") {
      setNewlyCompleted(false);
    }
  }, [task.stage]);

  // Scroll to block if blockRef is in URL
  useEffect(() => {
    const blockRef = searchParams.get("blockRef");
    if (blockRef && task.blocks.length > 0) {
      // Wait for blocks to render, then scroll
      setTimeout(() => {
        const blockElement = document.getElementById(`block-${blockRef}`);
        if (blockElement) {
          blockElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    }
  }, [searchParams, task.blocks.length]);

  const updateFormData = (t: EphemeralTask) => {
    setFormData((prevState) => ({
      ...(prevState || {}),
      ...t,
    }));
  };

  const createNewTask = async () => {
    _validate(formData);
    const selectedFamily = family
      ? family
      : families.find((f) => f.ref === selectedFamilyRef);

    await createTaskRequest(
      new CreateTaskRequest({
        familyRef: selectedFamily?.ref,
        title: formData.title,
        assigneeRef: selectedFamily?.advisorRef || task.assignee?.ref,
        priority: formData.priority || "medium",
        state: formData.state || TaskState.UNSPECIFIED,
        dueDate: formData.dueDate,
        sourceEntityType: formData.sourceEntityType,
        sourceEntityRef: formData.sourceEntityRef,
        hidden: task.hidden,
        isLightningTask: accountType === "member" && isLightningTask,
        details: {
          payload: content?.html || taskSuggestionDetails || "",
          textContent: content?.text || "",
          contentType: "html",
          attachments: content?.attachments || [],
        },
      }),
    );
  };

  return (
    <Box display="flex" flexDirection="column" id="task-details-panel">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          maxWidth: "850px",
        }}
      >
        {_isUnsavedTask && accountType === "advisor" && !family && (
          <Box>
            <RoundedDropDown
              initial={selectedFamilyRef}
              onChange={(s) => s?.value && setSelectedFamilyRef(s.value)}
              options={
                families.map((f) => ({
                  label: f.name,
                  value: f.ref,
                })) as Option[]
              }
              displayValue={
                families.find((f) => f.ref === selectedFamilyRef)?.name ||
                "Family (Required)"
              }
              sx={{
                fieldset: {
                  borderRadius: "8px",
                  border: !!formErrors?.familyRef
                    ? "2px solid #D32F2F !important"
                    : "2px solid #EAEBEC !important",
                },
                height: "52px",
              }}
            />
          </Box>
        )}
        <TextField
          variant="standard"
          fullWidth
          multiline
          id={"task-title-field"}
          disabled={createLoading || updateTaskStageLoading}
          error={!!formErrors?.title}
          value={formData.title || ""}
          onChange={(e) => updateFormData({ title: e.target.value })}
          margin="none"
          placeholder="Task name"
          InputProps={{
            disableUnderline: true,
            sx: (theme) => ({
              ...theme.typography.title,
              fontSize: "26px",
              fontFamily: "AlbertSans",
              paddingBottom: _isUnsavedTask ? "0px" : "20px", // To add clickable area
              border: formErrors?.title
                ? `2px solid ${theme.palette.error.main}`
                : _isUnsavedTask
                  ? `1px solid ${theme.palette.border}`
                  : "none",
              borderRadius: "8px",
              padding: "12px 0 12px",
              ".MuiInputBase-input": {
                paddingLeft: _isUnsavedTask ? "16px" : undefined,
              },
            }),
          }}
        />
        {_isUnsavedTask && (
          <Box>
            {/* For advisors this creates the description, while for members it adds a comment. */}
            <SimpleRichEditor
              forceOpen={true}
              disabled={createLoading}
              setContent={setContent}
              placeholder={"Add any details, notes or files to this task"}
              setUploadsInProgress={setUploadsInProgress}
              editorMinHeight="48px"
              initialContent={taskSuggestionDetails}
            />
          </Box>
        )}
        {_isUnsavedTask && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "8px" : "14px",
            }}
          >
            <Box>
              {accountType === "advisor" && (
                <TaskProperties
                  errors={formErrors}
                  formData={formData}
                  updateFormData={updateFormData}
                  onNewlyCompleted={() => setNewlyCompleted(true)}
                  key={"task-properties"}
                  accountType={accountType}
                  task={task}
                  taskCalendarEvents={taskCalendarEvents}
                  onAddEvent={() => setShowEventDialog(true)}
                  onEditEvent={(event) => {
                    if (event.sourceEntityType === "task_event") {
                      setEditingFullEvent(event);
                    } else {
                      setEditingEvent(event);
                    }
                  }}
                />
              )}
              {accountType === "member" && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => setIsLightningTask((prev) => !prev)}
                  >
                    <Checkbox
                      size="small"
                      checked={isLightningTask}
                      onChange={(e) => setIsLightningTask(e.target.checked)}
                      sx={{ padding: 0, marginRight: "6px" }}
                    />
                    <Typography variant="bodySmall">
                      Make it a lightning task
                    </Typography>
                  </Box>
                  <Typography
                    variant="bodySmall"
                    sx={{ color: "text.secondary", paddingLeft: "22px" }}
                  >
                    Lightning tasks are picked up by the first available advisor
                    for quick actions like a phone call.
                  </Typography>
                </Box>
              )}
            </Box>
            <Button
              sx={{ alignSelf: "flex-end  " }}
              onClick={createNewTask}
              disabled={!formData.title || uploadsInProgress || createLoading}
            >
              Save
            </Button>
          </Box>
        )}

        {/* All the components after we have a task saved in the backend - not a "new task" */}
        {!_isUnsavedTask && (
          <Box>
            {accountType === "advisor" &&
              task?.createdBy?.accountType === 2 && (
                <Typography
                  variant="body"
                  color="text.tertiary"
                  sx={{ marginBottom: "28px", marginTop: "-12px" }}
                >
                  Created by: {task?.createdBy?.displayName}
                </Typography>
              )}
            {task.hidden && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <EyeOff size={16} style={{ color: "info.main" }} />
                <Typography variant="bodySmall" sx={{ color: "text.tertiary" }}>
                  This task is hidden from the client.
                </Typography>
              </Box>
            )}
            {!isLightningDetectionMemberView && (
              <Box sx={{ marginBottom: "20px" }}>
                <TaskProperties
                  errors={formErrors}
                  formData={formData}
                  updateFormData={updateFormData}
                  onNewlyCompleted={() => setNewlyCompleted(true)}
                  key={"task-properties"}
                  accountType={accountType}
                  task={task}
                  taskCalendarEvents={taskCalendarEvents}
                  onAddEvent={() => setShowEventDialog(true)}
                  onEditEvent={(event) => {
                    if (event.sourceEntityType === "task_event") {
                      setEditingFullEvent(event);
                    } else {
                      setEditingEvent(event);
                    }
                  }}
                />
              </Box>
            )}
            {isLightningDetectionMemberView && <LightningDetectionNotice />}
            {isLightningDetectionAdvisorView && <LightningDetectionNotice />}
            {isLightningAdvisorFollowupView && <LightningNeedsAdvisorNotice />}
            {/* Show Start Agent button if no agent block exists and feature is enabled */}
            {!isLightningDetectionMemberView &&
              accountType === "advisor" &&
              // isFeatureEnabled("faye.task-agents", true) &&
              !task.blocks.some((block) => block.type === "agent") && (
                <StartAgentPrompt
                  onClick={() => {
                    onCreateTaskBlock(
                      "agent",
                      false,
                      undefined,
                      true,
                      true,
                      true,
                    );
                  }}
                  disabled={createTaskBlockLoading}
                />
              )}
            <BlockSection
              family={family}
              task={
                isLightningDetectionMemberView ? lightningDetectionTask : task
              }
              detailsRef={notesRef}
              doToast={doToast}
              accountType={accountType}
              updateTask={updateTask}
              onDeleteTaskBlock={onDeleteTaskBlock}
              refreshTask={refreshTask}
              refreshEvents={refreshEvents}
              delegationTasks={
                isLightningDetectionMemberView ? [] : delegationTasks
              }
              taskCalendarEvents={
                isLightningDetectionMemberView ? [] : taskCalendarEvents
              }
              taskExternalEvents={
                isLightningDetectionMemberView ? [] : taskExternalEvents
              }
            />
            {!isLightningDetectionMemberView && (
              <Box
                sx={{
                  display: "flex",
                  gap: isMobile ? "12px" : "20px",
                  marginTop: "8px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <CurrentUserAvatar
                    size={34}
                    autoscale={false}
                    clickable={false}
                  />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <SimpleTaskDocument
                    taskStage={task.stage as StageValue}
                    dropdownButtonId={`task-status-dropdown-${task.ref}`}
                    cacheKey={`task-comment-${task.ref}`}
                    disabled={updateTaskStageLoading || createTaskBlockLoading}
                    onSaveDocument={async (
                      content,
                      isShareable,
                      newStage,
                      holdDate,
                    ) => {
                      if (accountType === "member") {
                        isShareable = true;
                      }
                      let blockRef = null;
                      if (content) {
                        const newBlock = await onCreateTaskBlock(
                          "document",
                          isShareable,
                          content,
                        );
                        blockRef = newBlock?.block?.ref;
                      }
                      if (newStage) {
                        await updateTaskStage({
                          taskRef: task.ref,
                          stage: newStage,
                          blockRef: blockRef || "",
                          stageDate: holdDate || "",
                        });
                        if (newStage === "done") {
                          setNewlyCompleted(true);
                          updateFormData({
                            completionDateSec: BigInt(
                              Math.floor(Date.now() / 1000),
                            ),
                          });
                          celebrate(
                            doneButtonRef.current
                              ? getElementCenter(doneButtonRef.current)
                              : undefined,
                          );
                        }
                        await refreshTask();
                      }
                    }}
                    isCreate={true}
                    accountType={accountType}
                  />
                </Box>
              </Box>
            )}
            {accountType === "advisor" && (
              <Box sx={{ marginTop: "24px" }}>
                {task.stage === "done" && (
                  <TimeCompletion newlyCompleted={newlyCompleted} task={task} />
                )}
                <Divider sx={{ margin: "200px 0px 24px 0px" }} />
                <Footer
                  formData={formData}
                  updateFormData={updateFormData}
                  doneButtonRef={doneButtonRef}
                  onNewlyCompleted={() => setNewlyCompleted(true)}
                  onCreateDocument={() => {
                    onCreateTaskBlock("document", false, undefined, true);
                  }}
                  onCreateAgent={() => {
                    onCreateTaskBlock("agent", false, undefined, true, true);
                  }}
                  onAddTaskEvent={() => setShowFullEventDialog(true)}
                  onAddTaskReminder={() => setShowEventDialog(true)}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <SnackPack
        autoHideDuration={2000}
        snackPack={snackPack}
        alertSeverity="success"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
      <TaskReminderDialog
        open={showEventDialog}
        onClose={() => setShowEventDialog(false)}
        mode="create"
        taskTitle={task.title}
        loading={createEventLoading}
        hasDueDateEvent={!!task.dueDate}
        defaultIsDueDate={!task.dueDate && taskCalendarEvents.length === 0}
        onSave={async (
          title: string,
          eventDateTime: Date,
          isDueDate: boolean,
        ) => {
          const timestampSec = BigInt(
            Math.floor(eventDateTime.getTime() / 1000),
          );

          if (isDueDate) {
            // Update task due date
            const year = eventDateTime.getFullYear();
            const month = String(eventDateTime.getMonth() + 1).padStart(2, "0");
            const day = String(eventDateTime.getDate()).padStart(2, "0");
            const formattedDate = `${month}/${day}/${year}`;
            updateFormData({ dueDate: formattedDate });
          }

          // Create the calendar event
          await createCalendarEvent(
            new CreateCalendarEventRequest({
              familyRef: task.familyRef,
              title: title || task.title,
              description: new ContentUpdates({ contentType: "json" }),
              startSecUTC: timestampSec,
              endSecUTC: timestampSec,
              allDay: false,
              displayTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              reminders: [new EventReminder({ value: 0, unit: "minutes" })],
              sourceEntityType: "task_reminder",
              sourceEntityRef: task.ref,
              ...(isDueDate && {
                meta: JSON.stringify({ taskEventType: "dueDate" }),
              }),
            }),
          );
        }}
      />
      <TaskReminderDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        mode="edit"
        taskTitle={task.title}
        event={editingEvent}
        loading={updateEventLoading}
        hasDueDateEvent={
          !!task.dueDate &&
          !(
            editingEvent?.meta &&
            JSON.parse(editingEvent.meta).taskEventType === "dueDate"
          )
        }
        onDelete={async () => {
          if (!editingEvent) return;
          await deleteCalendarEvent(
            new DeleteCalendarEventRequest({
              eventRef: editingEvent.ref,
            }),
          );
          setEditingEvent(null);
        }}
        onSave={async (
          title: string,
          eventDateTime: Date,
          isDueDate: boolean,
        ) => {
          if (!editingEvent) return;

          if (isDueDate) {
            // Update task due date
            const year = eventDateTime.getFullYear();
            const month = String(eventDateTime.getMonth() + 1).padStart(2, "0");
            const day = String(eventDateTime.getDate()).padStart(2, "0");
            const formattedDate = `${month}/${day}/${year}`;
            updateFormData({ dueDate: formattedDate });
          }

          // Update the calendar event
          const timestampSec = BigInt(
            Math.floor(eventDateTime.getTime() / 1000),
          );
          await updateCalendarEvent(
            new UpdateCalendarEventRequest({
              eventRef: editingEvent.ref,
              updatedTitle: title || task.title,
              updatedStartSecUTC: timestampSec,
              updatedEndSecUTC: timestampSec,
              updatedAllDay: false,
              updatedReminders: [
                new EventReminder({ value: 0, unit: "minutes" }),
              ],
              updatedDisplayTimezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          );
          setEditingEvent(null);
        }}
      />
      <AddEventDialog
        open={showFullEventDialog}
        onClose={() => setShowFullEventDialog(false)}
        familyRef={task.familyRef}
        initialFormData={{
          familyRef: task.familyRef,
          sourceEntityType: "task_event",
          sourceEntityRef: task.ref,
        }}
        onCreated={() => {
          setShowFullEventDialog(false);
          if (task.ref) {
            listEventsBySource(
              new ListEventsBySourceRequest({
                sourceEntityType: "task_event",
                sourceEntityRef: task.ref,
              }),
            );
          }
        }}
        accountType={accountType}
      />
      {editingFullEvent && (
        <EditEventDialog
          eventRef={editingFullEvent.ref}
          open={!!editingFullEvent}
          onClose={() => setEditingFullEvent(null)}
          onEdited={() => {
            setEditingFullEvent(null);
            refreshTask();
          }}
          accountType={accountType}
        />
      )}
    </Box>
  );
};
